import { ContentType, SllLesson, SllModule } from '../../types';
import { useCallback, useMemo } from 'react';
import { useStandaloneContent } from '@src/hooks/useStandaloneContent';
import { useAnalytics, Events, useUser } from '@hooks';
type ContentElements = {
  contentId: string;
  lessonId?: string;
  content: SllModule;
  lesson?: SllLesson;
}
type ContentArr = {
  [lessonId: string]: ContentElements[];
};

function selectAdditionContent(modules: any[]) {
  if (modules.length > 0) {
    if (modules.at(0).content.type === ContentType.FORM || modules.at(0).content.name.toLowerCase().includes('exit ticket')) {
      modules.shift()
      return selectAdditionContent(modules)
    } else {
      return modules.at(0);
    }
  } else {
    return undefined
  }
}

export const useNextContent = () => {
  const { section, startedAlternateLesson, content, lesson, setLesson, setContent, setSelectionLevel, isPreview, isPreProgramQuiz, quizModules } = useStandaloneContent();
  const { trackEvent } = useAnalytics();
  const [user] = useUser();

  const contentArray: ContentArr = useMemo(() => {
    const res: ContentArr = {};

    if (section && startedAlternateLesson) {
      if (section?.id) {
        let modules = [...section?.modules || []]
        if (isPreview) {
          modules = modules.filter(module => module?.lastInLesson !== true);
        }
        modules.forEach(c => {
          if (!res[section?.id]) {
            res[section?.id] = [];
          }
          res[section?.id].push({
            contentId: c?.id || '',
            lessonId: section.id,
            content: c as SllModule,
            lesson,
          })
        })
      }
      return res;
    } else if (isPreProgramQuiz && quizModules) {
      let modules = [...quizModules || []]
      let OneQuizOnly = false   // We need to show only one Content on preprogram quiz progress
      modules.forEach(c => {
        if (!res['ppq']) {
          res['ppq'] = [];
        }
        if (isPreview) {
          res['ppq'].push({
            contentId: c?.id || '',
            lessonId: undefined,
            content: c as SllModule,
            lesson: undefined,
          })
        } else {
          if (!OneQuizOnly) {
            res['ppq'].push({
              contentId: c?.id || '',
              lessonId: undefined,
              content: c as SllModule,
              lesson,
            })
            if (c.type === ContentType.FORM) {
              OneQuizOnly = true
            }
          }
        }
      })
      return res;
    } else {
      if (lesson?.id) {
        let modules = [...lesson?.presentation || []]
        if (isPreview) {
          modules = modules.filter(module => module?.lastInLesson !== true);
        }
        modules.forEach(c => {
          if (!res[lesson?.id]) {
            res[lesson?.id] = [];
          }
          res[lesson?.id].push({
            contentId: c?.id || '',
            lessonId: lesson.id,
            content: c as SllModule,
            lesson,
          })
        })
      } else {
        let modules = [...lesson?.materials || []]
        modules.forEach(c => {
          if (!res['tp']) {
            res['tp'] = [];
          }
          res['tp'].push({
            contentId: c?.id || '',
            lessonId: undefined,
            content: c as SllModule,
            lesson: undefined,
          })
        })
      }
      return res;
    }
  }, [lesson, isPreview, section, startedAlternateLesson, isPreProgramQuiz, quizModules]);

  const nextContent = useMemo(() => {
    let arr: ContentElements[] = [];
    if (section && startedAlternateLesson) {
      if (!contentArray || !content) return undefined;
      arr = contentArray[section.id];
    } else if (isPreProgramQuiz && quizModules) {
      if (!contentArray || !content) return undefined;
      arr = contentArray['ppq'];
    }
    else if (lesson) {
      if (!contentArray || !content) return undefined;
      arr = contentArray[lesson.id];
    } else {
      if (!contentArray || !content) return undefined;
      arr = contentArray['tp'];
    }
    if (!arr || !content) return undefined
    const elIndex = arr.findIndex(c => c.contentId === content.id);
    if (elIndex === -1) {
      return undefined;
    }
    return elIndex === arr.length - 1 ? undefined : arr[elIndex]?.content;
  }, [contentArray, lesson, content, section, startedAlternateLesson, isPreProgramQuiz, quizModules]);

  const contentNext = useMemo(() => {
    if (!contentArray || !content) return undefined;
    if (!lesson) {
      return ({
        lessonId: "",
        moduleId: "",
        previousModuleId: undefined,
      });
    }
    let arr: ContentElements[] = [];
    if (section && startedAlternateLesson) {
      arr = contentArray[section.id];
    } else {
      arr = contentArray[lesson.id];
    }
    if (!arr || !content) return undefined
    const elIndex = arr.findIndex(c => c.contentId === content.id);
    return arr[elIndex + 1] ? {
      lessonId: arr[elIndex + 1].lessonId || "",
      moduleId: arr[elIndex + 1].contentId || "",
      previousModuleId: content.id,
      sectionId: section?.id,
    } : undefined;
  }, [contentArray, lesson, content, section, startedAlternateLesson]);

  const hasNextContent = !!nextContent;

  const hasPrevContent = useMemo(() => {
    let arr: ContentElements[] = [];
    if (section && startedAlternateLesson) {
      if (!contentArray || !content) return false;
      arr = contentArray[section.id];
    } else if (isPreProgramQuiz && quizModules) {
      if (!contentArray || !content) return false;
      arr = contentArray['ppq'];
    }
    else if (lesson) {
      if (!contentArray || !content) return false;
      arr = contentArray[lesson.id];
    } else {
      if (!contentArray || !content) return false;
      arr = contentArray['tp'];
    }
    if (!arr || !content) return false
    const elIndex = arr.findIndex(c => c.contentId === content.id);
    return elIndex > 0;
  }, [contentArray, lesson, content, section, startedAlternateLesson, isPreProgramQuiz, quizModules]);

  const hasAdditionalContent = useMemo(() => {
    let arr: ContentElements[] = [];
    if (lesson) {
      if (!contentArray || !content) return false;
      arr = contentArray[lesson.id];
    } else {
      if (!contentArray || !content) return false;
      arr = contentArray['tp'];
    }
    if (!arr || !content) return false

    const currentIndex = arr.findIndex(c => (c.contentId === content?.id)) + 1;
    const remainModules = arr.slice(currentIndex)
    var addition = selectAdditionContent(remainModules);

    if (!addition) return false

    if (addition.content.lastInLesson !== true) {
      return true
    }
    return false
  }, [contentArray, lesson, content]);

  const selectNext = useCallback((currentContent?: SllModule) => {
    const contentObj = currentContent || content;
    if (!contentObj) return;
    let arr: ContentElements[] = [];
    if (section && startedAlternateLesson) {
      arr = contentArray[section.id];
    } else if (isPreProgramQuiz && quizModules) {
      arr = contentArray['ppq'];
    }
    else if (lesson) {
      arr = contentArray[lesson.id];
    } else {
      arr = contentArray['tp'];
    }

    if (!arr) return false
    const currentIndex = arr.findIndex(c => c.contentId === contentObj.id) + 1;
    const next = arr[currentIndex];
    if (!!next) {
      setContent(next.content);
      setLesson(next.lesson);
      setSelectionLevel('Content');

      if (isPreview) {
        trackEvent(Events.actions.previewLessonModulesViewed, {
          standalone_lesson: true,
          lesson_name: next.lesson?.name,
          module_name: next.content?.name,
          schoolId: user?.schools?.[0]?.id,
          schoolName: user?.schools?.[0]?.name,
          userId: user?.id,
          userName: `${user?.firstName} ${user?.lastName}`,
        });
      } else {
        trackEvent(Events.actions.lessonModulesViewed, {
          standalone_lesson: true,
          lesson_name: next.lesson?.name,
          module_name: next.content?.name,
          schoolId: user?.schools?.[0]?.id,
          schoolName: user?.schools?.[0]?.name,
          userId: user?.id,
          userName: `${user?.firstName} ${user?.lastName}`,
          ...(next.lesson?.collections && next.lesson?.collections.length && {
            collection_name: next.lesson?.collections.filter(c => c?.collectionType === 'Theme')[0]?.name,
          }),
          ...(next.lesson?.multiCollections && next.lesson?.multiCollections.length && {
            program_name: next.lesson?.multiCollections[0]?.programType || '',
            multi_collection_name: next.lesson?.multiCollections.map(mc => mc?.name),
          }),
        });
      }
    }
  }, [content, section, startedAlternateLesson, lesson, contentArray, setContent, setLesson, setSelectionLevel, isPreview, trackEvent, isPreProgramQuiz, quizModules, user]);

  const selectExitTicket = useCallback((currentContent?: SllModule) => {
    const contentObj = currentContent || content;
    if (!contentObj) return;
    let arr: ContentElements[] = [];
    if (lesson) {
      arr = contentArray[lesson.id];
    } else {
      arr = contentArray['tp'];
    }

    if (!arr) return false
    const exitTicketIndex = arr.findIndex(c => c.content.type === ContentType.FORM || c.content.name.toLowerCase().includes('exit ticket'));
    const next = arr[exitTicketIndex];
    if (!!next) {
      setContent(next.content);
      setLesson(next.lesson);
      setSelectionLevel('Content');
      if (isPreview) {
        trackEvent(Events.actions.previewLessonModulesViewed, {
          standalone_lesson: true,
          lesson_name: next.lesson?.name,
          module_name: next.content?.name,
          schoolId: user?.schools?.[0]?.id,
          schoolName: user?.schools?.[0]?.name,
          userId: user?.id,
          userName: `${user?.firstName} ${user?.lastName}`,
        });
      } else {
        trackEvent(Events.actions.lessonModulesViewed, {
          standalone_lesson: true,
          lesson_name: next.lesson?.name,
          module_name: next.content?.name,
          schoolId: user?.schools?.[0]?.id,
          schoolName: user?.schools?.[0]?.name,
          userId: user?.id,
          userName: `${user?.firstName} ${user?.lastName}`,
          ...(next.lesson?.collections && next.lesson?.collections.length && {
            collection_name: next.lesson?.collections.filter(c => c?.collectionType === 'Theme')[0]?.name,
          }),
          ...(next.lesson?.multiCollections && next.lesson?.multiCollections.length && {
            program_name: next.lesson?.multiCollections[0]?.programType || '',
            multi_collection_name: next.lesson?.multiCollections.map(mc => mc?.name),
          }),
        });
      }
    }
  }, [content, lesson, contentArray, setContent, setLesson, setSelectionLevel, isPreview, trackEvent, user]);

  const selectAdditionalContent = useCallback((currentContent?: SllModule) => {
    const contentObj = currentContent || content;
    if (!contentObj) return;
    let arr: ContentElements[] = [];
    if (lesson) {
      arr = contentArray[lesson.id];
    } else {
      arr = contentArray['tp'];
    }

    if (!arr) return false
    const currentIndex = arr.findIndex(c => (c.contentId === contentObj.id)) + 1;
    const remainModules = arr.slice(currentIndex)
    var next = selectAdditionContent(remainModules);
    if (!!next) {
      setContent(next.content);
      setLesson(next.lesson);
      setSelectionLevel('Content');
      if (isPreview) {
        trackEvent(Events.actions.previewLessonModulesViewed, {
          standalone_lesson: true,
          lesson_name: next.lesson?.name,
          module_name: next.content?.name,
          schoolId: user?.schools?.[0]?.id,
          schoolName: user?.schools?.[0]?.name,
          userId: user?.id,
          userName: `${user?.firstName} ${user?.lastName}`,
        });
      } else {
        trackEvent(Events.actions.lessonModulesViewed, {
          standalone_lesson: true,
          lesson_name: next.lesson?.name,
          module_name: next.content?.name,
          schoolId: user?.schools?.[0]?.id,
          schoolName: user?.schools?.[0]?.name,
          userId: user?.id,
          userName: `${user?.firstName} ${user?.lastName}`,
          ...(next.lesson?.collections && next.lesson?.collections.length && {
            collection_name: next.lesson?.collections.filter(c => c?.collectionType === 'Theme')[0]?.name,
          }),
          ...(next.lesson?.multiCollections && next.lesson?.multiCollections.length && {
            program_name: next.lesson?.multiCollections[0]?.programType || '',
            multi_collection_name: next.lesson?.multiCollections.map(mc => mc?.name),
          }),
        });
      }
    }
  }, [content, lesson, contentArray, setContent, setLesson, setSelectionLevel, isPreview, trackEvent, user]);

  const selectPrev = useCallback(() => {
    if (!content) return;
    let arr: ContentElements[] = [];
    if (section && startedAlternateLesson) {
      arr = contentArray[section.id];
    } else if (isPreProgramQuiz && quizModules) {
      arr = contentArray['ppq'];
    }
    else if (lesson) {
      arr = contentArray[lesson.id];
    } else {
      arr = contentArray['tp'];
    }
    if (!arr || !content) return false
    const currentIndex = arr.findIndex(c => c.contentId === content.id);
    const next = arr[currentIndex - 1];
    if (next) {
      setContent(next.content);
      setLesson(next.lesson);
      setSelectionLevel('Content');
      if (isPreview) {
        trackEvent(Events.actions.previewLessonModulesViewed, {
          standalone_lesson: true,
          lesson_name: next.lesson?.name,
          module_name: next.content?.name,
          schoolId: user?.schools?.[0]?.id,
          schoolName: user?.schools?.[0]?.name,
          userId: user?.id,
          userName: `${user?.firstName} ${user?.lastName}`,
        });
      } else {
        trackEvent(Events.actions.lessonModulesViewed, {
          standalone_lesson: true,
          lesson_name: next.lesson?.name,
          module_name: next.content?.name,
          schoolId: user?.schools?.[0]?.id,
          schoolName: user?.schools?.[0]?.name,
          userId: user?.id,
          userName: `${user?.firstName} ${user?.lastName}`,
          ...(next.lesson?.collections && next.lesson?.collections.length && {
            collection_name: next.lesson?.collections.filter(c => c?.collectionType === 'Theme')[0]?.name,
          }),
          ...(next.lesson?.multiCollections && next.lesson?.multiCollections.length && {
            program_name: next.lesson?.multiCollections[0]?.programType || '',
            multi_collection_name: next.lesson?.multiCollections.map(mc => mc?.name),
          }),
        });
      }
    }
  }, [content, section, startedAlternateLesson, lesson, contentArray, setContent, setLesson, setSelectionLevel, isPreview, trackEvent, isPreProgramQuiz, quizModules, user]);

  return {
    hasNextContent,
    hasPrevContent,
    hasAdditionalContent,
    selectNext,
    selectPrev,
    selectExitTicket,
    selectAdditionalContent,
    nextContent,
    contentNext,
    content,
  };
}
